import { useQuery } from '@tanstack/react-query';
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { queryGlobalSearch, type GlobalSearchResponse } from '@/features/search/data';
import { queryKeys } from '@/shared/hooks';
import { componentSizes } from '@/theme/componentSizes';
import { iconSizes, r } from '@/theme/tokens';
import { Box, CircularProgress, IconButton, Popover, SearchIcon, Stack, Text } from '@/ui';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import {
  buildGlobalSearchEmptyRowSx,
  buildGlobalSearchDesktopFieldFrameSx,
  buildGlobalSearchDesktopIconButtonSx,
  buildGlobalSearchDesktopShellSx,
  buildGlobalSearchLoadingRowSx,
  buildGlobalSearchPopoverPaperSx,
  buildGlobalSearchResultGroupSx,
  buildGlobalSearchResultIconFrameSx,
  buildGlobalSearchResultItemSx,
  buildGlobalSearchResultsSummarySx,
  buildGlobalSearchResultSummaryIconSx,
  buildGlobalSearchSearchFieldSx,
  globalSearchCompactFieldFrameSx,
  globalSearchErrorRowSx,
  globalSearchErrorTextSx,
  globalSearchHighlightSx,
  globalSearchInheritedTextSx,
  globalSearchLoadingTextSx,
  globalSearchPopoverRootSx,
  globalSearchResultIconSx,
  globalSearchResultMetadataSx,
  globalSearchResultsListSx,
  globalSearchResultTextFrameSx,
  globalSearchResultTitleSx,
} from './GlobalSearch.styles';
import {
  getGlobalSearchResultIcon,
  globalSearchResultTypeLabels,
  matchNavigationResults,
} from './globalSearchNavigation';
import { toGlobalSearchEntityViewResult, type GlobalSearchViewResult } from './globalSearchRouting';
import type { ApiError } from '@/shared/data/client';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';

type GlobalSearchProps = {
  userId: string | null;
  isSuperuser: boolean;
  featureFlags?: FeatureFlagResolveResult['flags'] | null;
};

const toolbarSearchWidth = 260;
const toolbarSearchWidthPx = `${toolbarSearchWidth}px`;
const toolbarSearchHeight = componentSizes.filters.toolbarControlHeight;
const toolbarSearchRadius = r('sm');
const toolbarSearchFontSize = componentSizes.filters.toolbarFontSize;
const toolbarSearchBorderColor = 'var(--surface-border)';
const toolbarSearchSurfaceBackground = 'var(--surface-page)';
const toolbarSearchButtonIconSize = iconSizes.lg;
const inputAdornmentIconSize = iconSizes.md;
const resultIconContainerSize = iconSizes.lg;
const resultStatusRowHeight = iconSizes.xxl;
const minSearchLength = 2;
const debounceMs = 200;
const desktopPopoverWidth = 360;

const getEntityProjectName = (result: GlobalSearchViewResult): string | null => {
  if (result.source !== 'entity' || result.type === 'project' || result.subtitle === null) {
    return null;
  }

  const parts = result.subtitle
    .split('·')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (parts.length >= 3) return parts[1] ?? null;
  if (parts.length >= 2) return parts[0] ?? null;

  return null;
};

const formatResultMetadata = (result: GlobalSearchViewResult): string | null => {
  const typeLabel = globalSearchResultTypeLabels[result.type];

  if (result.source === 'navigation') {
    return result.subtitle ?? typeLabel;
  }

  const projectName = getEntityProjectName(result);
  return projectName ? `${projectName} · ${typeLabel}` : typeLabel;
};

const buildHighlightedText = (value: string, query: string): ReactNode => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) return value;

  const normalizedValue = value.toLowerCase();
  const parts: ReactNode[] = [];
  let cursor = 0;
  let matchIndex = normalizedValue.indexOf(normalizedQuery);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      parts.push(value.slice(cursor, matchIndex));
    }
    const matchEnd = matchIndex + normalizedQuery.length;
    parts.push(
      <Box key={`${matchIndex}-${matchEnd}`} component="span" sx={globalSearchHighlightSx}>
        {value.slice(matchIndex, matchEnd)}
      </Box>,
    );
    cursor = matchEnd;
    matchIndex = normalizedValue.indexOf(normalizedQuery, cursor);
  }

  if (cursor < value.length) {
    parts.push(value.slice(cursor));
  }

  return parts;
};

const groupResults = (
  results: GlobalSearchViewResult[],
): Array<{ source: GlobalSearchViewResult['source']; results: GlobalSearchViewResult[] }> => {
  const sources: Array<GlobalSearchViewResult['source']> = ['navigation', 'entity'];
  return sources.flatMap((source) => {
    const sourceResults = results.filter((result) => result.source === source);
    return sourceResults.length > 0 ? [{ source, results: sourceResults }] : [];
  });
};

const isBrowserNavigationClick = (event: MouseEvent<HTMLElement>): boolean =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

export const GlobalSearch = ({
  userId,
  isSuperuser,
  featureFlags,
}: GlobalSearchProps): ReactElement | null => {
  const layoutMode = useLayoutMode();
  const navigate = useNavigate();
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [desktopSearchExpanded, setDesktopSearchExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const desktopSearchRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const isCompact = layoutMode !== layoutModeTokens.wide;
  const trimmedSearch = searchValue.trim();
  const debouncedTrimmedSearch = debouncedSearchValue.trim();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, debounceMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [searchValue]);

  const entityQuery = useQuery<GlobalSearchResponse, ApiError>({
    queryKey: queryKeys.globalSearch(
      debouncedTrimmedSearch.length >= minSearchLength ? debouncedTrimmedSearch : null,
      selectedOrganisationId,
      selectedProjectId,
      userId,
    ),
    enabled: userId !== null && debouncedTrimmedSearch.length >= minSearchLength,
    queryFn: async () =>
      await queryGlobalSearch({
        query: debouncedTrimmedSearch,
        organisationId: selectedOrganisationId ?? null,
        projectId: selectedProjectId ?? null,
      }),
    staleTime: 10_000,
  });

  const navigationMatches = useMemo(
    () => matchNavigationResults(trimmedSearch, isSuperuser, featureFlags),
    [featureFlags, isSuperuser, trimmedSearch],
  );
  const searchSettling =
    trimmedSearch.length >= minSearchLength && debouncedTrimmedSearch !== trimmedSearch;
  const entityResults = useMemo(
    () =>
      searchSettling ? [] : (entityQuery.data?.results ?? []).map(toGlobalSearchEntityViewResult),
    [entityQuery.data?.results, searchSettling],
  );
  const visibleResults = useMemo(
    () => [...navigationMatches, ...entityResults],
    [entityResults, navigationMatches],
  );
  const resultGroups = useMemo(() => groupResults(visibleResults), [visibleResults]);
  const open = anchorElement !== null;

  useEffect(() => {
    setActiveIndex(null);
  }, [trimmedSearch, visibleResults.length]);

  useEffect(() => {
    if (activeIndex === null) return;
    const activeResult = document.getElementById(`global-search-result-${String(activeIndex)}`);
    if (typeof activeResult?.scrollIntoView === 'function') {
      activeResult.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  useEffect(() => {
    if (isCompact || !desktopSearchExpanded) return;

    const input = inputRef.current;
    if (input === null) return;

    input.focus();
    const caretPosition = input.value.length;
    input.setSelectionRange(caretPosition, caretPosition);
  }, [desktopSearchExpanded, isCompact]);

  useEffect(() => {
    if (typeof document === 'undefined' || isCompact || !desktopSearchExpanded) return;

    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (desktopSearchRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('#global-search-results')) return;

      closeSearch();
      setSearchFocused(false);
      setDesktopSearchExpanded(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
    };
  }, [desktopSearchExpanded, isCompact]);

  if (userId === null) {
    return null;
  }

  const closeSearch = (): void => {
    setAnchorElement(null);
    setActiveIndex(null);
  };

  const openSearch = (element: HTMLElement): void => {
    setAnchorElement(isCompact ? element : (desktopSearchRef.current ?? element));
    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const expandDesktopSearch = (): void => {
    setDesktopSearchExpanded(true);
  };

  const openDesktopSearchForValue = (element: HTMLElement, value: string): void => {
    if (value.trim().length === 0) {
      closeSearch();
      return;
    }
    openSearch(element);
  };

  const handleSelectResult = (result: GlobalSearchViewResult): void => {
    if (result.source === 'entity') {
      if (result.context.organisationId !== selectedOrganisationId) {
        setSelectedOrganisationId(result.context.organisationId);
      }
      if (result.context.projectId !== selectedProjectId) {
        setSelectedProjectId(result.context.projectId);
      }
    }
    closeSearch();
    setSearchValue('');
    setDebouncedSearchValue('');
    void navigate(result.href);
    setDesktopSearchExpanded(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const nextValue = event.target.value;
    setSearchValue(nextValue);
    if (!open) {
      openDesktopSearchForValue(event.currentTarget, nextValue);
    } else if (!isCompact && nextValue.trim().length === 0) {
      closeSearch();
    }
  };

  const handleClearSearch = (): void => {
    setSearchValue('');
    setDebouncedSearchValue('');
    closeSearch();
    if (isCompact) return;

    setDesktopSearchExpanded(true);
    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (visibleResults.length === 0) return;
      setActiveIndex((current) =>
        current === null ? 0 : Math.min(current + 1, visibleResults.length - 1),
      );
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (visibleResults.length === 0) return;
      setActiveIndex((current) =>
        current === null ? visibleResults.length - 1 : Math.max(current - 1, 0),
      );
      return;
    }
    if (event.key === 'Home') {
      if (visibleResults.length === 0) return;
      event.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (event.key === 'End') {
      if (visibleResults.length === 0) return;
      event.preventDefault();
      setActiveIndex(visibleResults.length - 1);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearch();
      setDesktopSearchExpanded(false);
      window.setTimeout(() => {
        triggerRef.current?.focus();
      }, 0);
      return;
    }
    if (event.key === 'Enter') {
      if (visibleResults.length === 0 || activeIndex === null) {
        return;
      }
      const result = visibleResults[activeIndex];
      event.preventDefault();
      handleSelectResult(result);
    }
  };

  const showLoading =
    searchSettling || (entityQuery.isFetching && debouncedTrimmedSearch.length >= minSearchLength);
  const showEmpty =
    trimmedSearch.length >= minSearchLength &&
    !showLoading &&
    !entityQuery.isError &&
    visibleResults.length === 0;
  const showResultsSummary = visibleResults.length > 0;
  const popoverWidth = isCompact
    ? 'min(88vw, 390px)'
    : showLoading || showEmpty
      ? toolbarSearchWidth
      : desktopPopoverWidth;

  const searchField = (
    <SearchFilterField
      value={searchValue}
      placeholder="Search..."
      onChange={handleInputChange}
      onClear={handleClearSearch}
      inputAriaLabel="Global Search"
      inputBackgroundColor={toolbarSearchSurfaceBackground}
      controlHeight={toolbarSearchHeight}
      compact
      focused={!isCompact && searchFocused}
      leadingSearchAdornment
      trailingSearchAdornment={false}
      filledClearIcon={false}
      slotProps={{
        htmlInput: {
          ref: (instance: HTMLInputElement | null) => {
            inputRef.current = instance;
          },
          role: 'combobox',
          'aria-expanded': open,
          'aria-controls': open ? 'global-search-results' : undefined,
          'aria-activedescendant':
            activeIndex === null ? undefined : `global-search-result-${String(activeIndex)}`,
          onFocus: (event: FocusEvent<HTMLInputElement>) => {
            setSearchFocused(true);
            if (isCompact) {
              openSearch(event.currentTarget);
              return;
            }
            openDesktopSearchForValue(event.currentTarget, searchValue);
          },
          onBlur: () => {
            setSearchFocused(false);
          },
          onKeyDown: handleKeyDown,
        },
      }}
      sx={buildGlobalSearchSearchFieldSx({
        inputAdornmentIconSize,
        isCompact,
        toolbarSearchBorderColor,
        toolbarSearchFontSize,
        toolbarSearchHeight,
        toolbarSearchRadius,
        toolbarSearchWidthPx,
      })}
    />
  );
  const desktopSearchField = (
    <Box
      ref={desktopSearchRef}
      sx={buildGlobalSearchDesktopShellSx({
        desktopSearchExpanded,
        toolbarSearchHeight,
        toolbarSearchWidth,
      })}
    >
      {desktopSearchExpanded ? (
        <Box sx={buildGlobalSearchDesktopFieldFrameSx({ toolbarSearchWidth })}>{searchField}</Box>
      ) : null}
      {desktopSearchExpanded ? null : (
        <IconButton
          ref={triggerRef}
          aria-label="Expand global search"
          onClick={() => {
            expandDesktopSearch();
          }}
          sx={buildGlobalSearchDesktopIconButtonSx({
            toolbarSearchButtonIconSize,
            toolbarSearchHeight,
          })}
        >
          <SearchIcon fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      {isCompact ? (
        <IconButton
          ref={triggerRef}
          aria-label="Open global search"
          onClick={(event) => {
            openSearch(event.currentTarget);
          }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      ) : (
        desktopSearchField
      )}
      <Popover
        open={open}
        anchorEl={anchorElement}
        onClose={() => {
          closeSearch();
          setDesktopSearchExpanded(false);
          window.setTimeout(() => {
            triggerRef.current?.focus();
          }, 0);
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        minWidth={popoverWidth}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          root: {
            sx: globalSearchPopoverRootSx,
          },
        }}
        paperSx={buildGlobalSearchPopoverPaperSx({ width: popoverWidth })}
      >
        <Box id="global-search-results" role="listbox" sx={globalSearchResultsListSx}>
          {isCompact ? <Box sx={globalSearchCompactFieldFrameSx}>{searchField}</Box> : null}
          {showResultsSummary ? (
            <Box sx={buildGlobalSearchResultsSummarySx({ height: resultStatusRowHeight })}>
              <SearchIcon sx={buildGlobalSearchResultSummaryIconSx({ resultIconContainerSize })} />
              <Text role="bodyStrong" component="div" sx={globalSearchInheritedTextSx}>
                {visibleResults.length} {visibleResults.length === 1 ? 'result' : 'results'} found
              </Text>
            </Box>
          ) : null}
          {resultGroups.map((group, groupIndex) => (
            <Box
              key={group.source}
              sx={buildGlobalSearchResultGroupSx({ isFirstGroup: groupIndex === 0 })}
            >
              <Stack gap="xxs">
                {group.results.map((result) => {
                  const ResultIcon = getGlobalSearchResultIcon(result);
                  const resultIndex = visibleResults.findIndex((item) => item.id === result.id);
                  const selected = resultIndex === activeIndex;
                  const metadata = formatResultMetadata(result);
                  return (
                    <Box
                      key={result.id}
                      id={`global-search-result-${String(resultIndex)}`}
                      component="a"
                      href={result.href}
                      role="option"
                      aria-selected={selected}
                      tabIndex={-1}
                      onMouseDown={(event) => {
                        if (!isBrowserNavigationClick(event)) {
                          event.preventDefault();
                        }
                      }}
                      onClick={(event) => {
                        if (isBrowserNavigationClick(event)) return;

                        event.preventDefault();
                        handleSelectResult(result);
                      }}
                      sx={buildGlobalSearchResultItemSx({ selected })}
                    >
                      <Box
                        sx={buildGlobalSearchResultIconFrameSx({
                          resultIconContainerSize,
                          selected,
                        })}
                      >
                        <ResultIcon sx={globalSearchResultIconSx} />
                      </Box>
                      <Box sx={globalSearchResultTextFrameSx}>
                        <Text
                          role="bodyStrong"
                          component="div"
                          truncate
                          sx={globalSearchResultTitleSx}
                        >
                          {buildHighlightedText(result.title, trimmedSearch)}
                        </Text>
                        {metadata ? (
                          <Text
                            role="bodyMuted"
                            component="div"
                            truncate
                            sx={globalSearchResultMetadataSx}
                          >
                            {buildHighlightedText(metadata, trimmedSearch)}
                          </Text>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
          {showLoading ? (
            <Box sx={buildGlobalSearchLoadingRowSx({ height: resultStatusRowHeight })}>
              <CircularProgress size={16} />
              <Text role="bodyMuted" sx={globalSearchLoadingTextSx}>
                Searching
              </Text>
            </Box>
          ) : null}
          {entityQuery.isError ? (
            <Box sx={globalSearchErrorRowSx}>
              <Text role="body" sx={globalSearchErrorTextSx}>
                Search failed
              </Text>
            </Box>
          ) : null}
          {showEmpty ? (
            <Box sx={buildGlobalSearchEmptyRowSx({ height: resultStatusRowHeight })}>
              <Text role="bodyMuted" component="div">
                No results
              </Text>
            </Box>
          ) : null}
        </Box>
      </Popover>
    </>
  );
};
